import { Box, FormControl, TextField } from '@mui/material';
import { DataGrid } from 'devextreme-react';
import ReactDOM from "react-dom/client";
import { DashboardApis } from 'hooks/apis/DashboardApis';
import { IDashboardClient } from 'interfaces/Dashboard/IDashboardClient';
import React, { useMemo, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import VisibilityIcon from '@mui/icons-material/Visibility';
import PageTitle from '../../../components/PageTitle';
import './AllClientDashboards.scss';

// Static — never depend on component state, so hoist entirely outside the component
const scrollingConfig = { mode: "virtual" as const, preloadEnabled: true }
const selectionConfig = { mode: "single" as const };
const groupingConfig = { autoExpandAll: true, allowCollapsing: true };
const headerFilterConfig = { visible: true };
const pagingConfig = { enabled: false };

const AllClientDashboards = () => {
  const navigate = useNavigate();
  const pageGrid = useRef(null);
  const { getAllDashboardClients } = DashboardApis();
  const [dashboardClients, setDashboardClients] = useState<IDashboardClient[] | undefined>(null);
  const [textFilter, setTextFilter] = useState<string>('');

  const searchPanelConfig = useMemo(() => ({ visible: false, text: textFilter }), [textFilter]);

 const columns = useMemo(() => [
  {
    dataField: "ClientName",
    dataType: "string" as const,
    calculateCellValue: (rowData: IDashboardClient) =>
      rowData.LimsDatabase === "Envirolims"
        ? `${rowData.ClientName} (${rowData.ClientReference})`
        : rowData.ClientName,
  },
  { dataField: "LimsDatabase", caption: "Database", dataType: "string" as const },
  {
    cssClass: "linkCell",
    caption: "",
    width: 100,
    alignment: "center" as const,
    dataType: "string" as const,
    cellTemplate: (container: HTMLElement, options: any) => {
      const encodedClient = encodeURIComponent(options?.data?.ClientReference);
      const encodedLimsDatabase = encodeURIComponent(options?.data?.LimsDatabase);
      const url = `/dashboard/${encodedLimsDatabase}/${encodedClient}`;
      const linkElement = (
        <a href={url} className="view-link" onClick={(e) => {
          if (e.defaultPrevented || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) {
            return;
          }
          e.preventDefault();
          navigate(url);
        }}
    >
          <VisibilityIcon /> <span>View</span>
        </a>
      );
      while (container.firstChild) {
        container.removeChild(container.firstChild);
      }
      const root = ReactDOM.createRoot(container);
      root.render(linkElement);
    },
  },
], [navigate]);

  useEffect(() => {
  const instance = pageGrid.current?.instance;
  if (!instance) return;
  instance.getScrollable()?.scrollTo({ top: 0 });
}, [textFilter]);

  useEffect(() => {
    getAllDashboardClients().then((data) => {
      if (!data.Message) {
        setDashboardClients(data);
      } else {
        // Do something with the error message on the page
      }
    });
  }, [getAllDashboardClients]);

  return (
    <>
      <PageTitle pageTitle="Client Stats" />
      <Box>
        <Box className="searchbar">
          <FormControl fullWidth>
            <TextField
              value={textFilter}
              onChange={(item) => setTextFilter(item?.target?.value)}
              size="small"
              label="Filter Client"
              fullWidth
            />
          </FormControl>
        </Box>
        <Box className="clientDashboards">
          <DataGrid
            ref={pageGrid}
            height={800}
            dataSource={dashboardClients}
            allowColumnReordering={true}
            searchPanel={searchPanelConfig}
            rowAlternationEnabled={true}
            showBorders={false}
            wordWrapEnabled={true}
            hoverStateEnabled={false}
            onRowDblClick={(e) =>
              navigate(`/dashboard/${encodeURIComponent(e?.data?.LimsDatabase)}/${encodeURIComponent(e?.data?.ClientReference)}`)
            }
            paging={pagingConfig}
            groupPanel={{ visible: true }}
            grouping={groupingConfig}
            scrolling={scrollingConfig}
            headerFilter={headerFilterConfig}
            selection={selectionConfig}
            columns={columns}
          />
        </Box>
      </Box>
    </>
  );
};

export default AllClientDashboards;
